import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import styled, { css } from "styled-components";

import {
  DAVYS_GREY_100,
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_800,
  MOSS_GREEN_800,
} from "./colors";
import { Button, IconButton } from "./Buttons";
import FlexBox from "./FlexBox";
import { Body1, Body2, H2, H3 } from "./Headings";
import { Modal } from "./Modal";

dayjs.extend(isSameOrBefore);

const Header = styled(FlexBox)`
  padding: 1rem;
  border-bottom: 1px solid ${DAVYS_GREY_400};
  justify-content: space-between;
  align-items: center;
`;

const CloseBtn = styled(FlexBox)`
  width: 1.25rem;
  height: 1.25rem;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Content = styled(FlexBox)`
  flex-direction: column;
  row-gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid ${DAVYS_GREY_400};
`;

const DateSelection = styled(FlexBox)`
  justify-content: space-between;
  align-items: center;
`;

const TimeSelection = styled(FlexBox)`
  align-self: center;
  align-items: center;
  column-gap: 1rem;
`;

const TimeSlider = styled(FlexBox)`
  position: relative;
  height: 7.5rem;
  overflow-y: hidden;
  ::after {
    content: "";
    pointer-events: none;
    position: absolute;
    inset: 0;
    background: linear-gradient(white, transparent 33% 67%, white);
  }
`;

const TimeList = styled(FlexBox)`
  position: absolute;
  top: 2.5rem;
  flex-direction: column;
  transform: ${({ offset }) => `translateY(${-offset}px)`};
  transition: ${({ showTransition }) =>
    showTransition ? "transform 0.3s" : "none"};
`;

const SelectionSlot = styled.div`
  align-self: center;
  width: 3.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${DAVYS_GREY_400};
  background-color: ${DAVYS_GREY_200};
`;

const Slot = styled(FlexBox)`
  justify-content: center;
  align-items: center;
  width: 3.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  cursor: pointer;
`;

const DisplayTime = styled(H2)`
  color: ${({ selected }) => (selected ? MOSS_GREEN_800 : DAVYS_GREY_800)};
`;

const Meridiem = styled.div`
  padding: 0.25rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid ${DAVYS_GREY_400};
  background-color: ${DAVYS_GREY_100};
  text-transform: uppercase;

  ${({ isSelected }) =>
    isSelected &&
    css`
      border-color: ${MOSS_GREEN_800};
      background-color: ${MOSS_GREEN_800};
      ${Body2} {
        color: ${DAVYS_GREY_100};
      }
    `}

  ${({ isSelected }) =>
    !isSelected &&
    css`
      :hover {
        background-color: ${DAVYS_GREY_200};
        cursor: pointer;
      }
    `}
`;

const NavBtn = styled(IconButton)`
  min-width: auto;
  padding: 0;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  stroke-width: 3;
`;

const Footer = styled(FlexBox)`
  padding: 1rem;
  justify-content: space-between;
  align-items: center;
`;

const TimePicker = ({ title, onTimeSelect, closeTimePicker }) => {
  const currentMinute = dayjs().minute();
  const minuteDiff = 5 - (currentMinute % 5);
  const nextAvailableDate = dayjs()
    .minute(currentMinute + minuteDiff)
    .second(0);

  const [selectedDateTime, setSelectedDateTime] = useState(nextAvailableDate);
  const [isAm, setIsAm] = useState(nextAvailableDate.hour() < 12);

  const [hourScrollOffset, setHourScrollOffset] = useState(0);
  const [minuteScrollOffset, setMinuteScrollOffset] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);

  const selectedHour = nextAvailableDate.hour();
  const selectedMinute = nextAvailableDate.minute();
  const hourIn12HourFormat = selectedHour % 12 || 12;
  const [selectedHourIndex, setSelectedHourIndex] = useState(
    hourIn12HourFormat - 1
  );
  const [selectedMinuteIndex, setSelectedMinuteIndex] = useState(
    selectedMinute / 5
  );

  const isManualScrollRef = useRef(false);
  const throttleTimerRef = useRef(null);

  const [listItemHeight, setListItemHeight] = useState(0);
  const hourTranslateY = selectedHourIndex * listItemHeight;
  const minuteTranslateY = selectedMinuteIndex * listItemHeight;

  const isCtaDisabled = selectedDateTime.isSameOrBefore(dayjs());

  useEffect(() => {
    setHourScrollOffset(hourTranslateY);
    setMinuteScrollOffset(minuteTranslateY);
  }, [hourTranslateY, minuteTranslateY]);

  const hours = Array(12)
    .fill(1)
    .map((_, i) => i + 1);

  const minutes = Array(12)
    .fill(1)
    .map((_, i) => i * 5);

  const setItemHeight = elem => {
    if (!elem || !!listItemHeight) return;
    setListItemHeight(elem.clientHeight);
  };

  const handlePrevClick = () =>
    setSelectedDateTime(selectedDateTime.subtract(1, "day"));

  const handleNextClick = () =>
    setSelectedDateTime(selectedDateTime.add(1, "day"));

  const formatTime = time => {
    if (time < 10) return "0" + time;
    return String(time);
  };

  const getCalculatedScrollInfo = (deltaY, offset) => {
    if (!listItemHeight) return false;
    if (!isManualScrollRef.current) isManualScrollRef.current = true;
    const maxOffset = listItemHeight * 11;
    const actualOffset = deltaY + offset;
    const scrollOffset = Math.max(0, Math.min(actualOffset, maxOffset));
    const selectedIndex = Math.round(scrollOffset / listItemHeight);
    return { scrollOffset, selectedIndex };
  };

  const getHourIn24HourFormat = hour => {
    let convertedHour = hour;
    if (hour === 12) convertedHour = 0;
    if (!isAm) convertedHour += 12;
    return convertedHour;
  };

  const handleHourScroll = deltaY => {
    const scrollInfo = getCalculatedScrollInfo(deltaY, hourScrollOffset);
    if (!scrollInfo) return;
    const { scrollOffset, selectedIndex } = scrollInfo;
    let selectedHour = hours[selectedIndex];
    const hourIn24HourFormat = getHourIn24HourFormat(selectedHour);
    setHourScrollOffset(scrollOffset);
    setSelectedHourIndex(selectedIndex);
    setSelectedDateTime(selectedDateTime.hour(hourIn24HourFormat));
  };

  const handleMinuteScroll = deltaY => {
    const scrollInfo = getCalculatedScrollInfo(deltaY, minuteScrollOffset);
    if (!scrollInfo) return;
    const { scrollOffset, selectedIndex } = scrollInfo;
    const selectedMinute = minutes[selectedIndex];
    setMinuteScrollOffset(scrollOffset);
    setSelectedMinuteIndex(selectedIndex);
    setSelectedDateTime(selectedDateTime.minute(selectedMinute));
  };

  const handleAmClick = () => {
    if (!isAm) {
      setIsAm(true);
      setSelectedDateTime(selectedDateTime.subtract(12, "hour"));
    }
  };

  const handlePmClick = () => {
    if (isAm) {
      setIsAm(false);
      setSelectedDateTime(selectedDateTime.add(12, "hour"));
    }
  };

  const handleHourSelection = hour => {
    setSelectedHourIndex(hour - 1);
    const hourIn24HourFormat = getHourIn24HourFormat(hour);
    setSelectedDateTime(selectedDateTime.hour(hourIn24HourFormat));
  };

  const handleMinuteSelection = minute => {
    setSelectedMinuteIndex(minute / 5);
    setSelectedDateTime(selectedDateTime.minute(minute));
  };

  const handleTouchStart = e => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
  };

  const handleTouchMove = (clientY, handleScroll) => {
    if (throttleTimerRef.current) return;
    const deltaY = touchStartY - clientY;
    handleScroll(deltaY);
    throttleTimerRef.current = setTimeout(() => {
      throttleTimerRef.current = null;
    }, 150);
  };

  return (
    <Modal
      XS
      width="27rem"
      height="auto"
      mobileWidth="90%"
      mobileHeight="auto"
      mobileBorderRadius="1rem"
      borderRadius="0.5rem"
    >
      <Header>
        <Body1 bold>{title}</Body1>
        <CloseBtn onClick={closeTimePicker}>
          <FiX color={DAVYS_GREY_800} strokeWidth={3} />
        </CloseBtn>
      </Header>
      <Content>
        <DateSelection>
          <NavBtn
            Icon={FiChevronLeft}
            tertiary
            outline
            disabled={dayjs().isSame(selectedDateTime, "date")}
            onClick={handlePrevClick}
          />
          <Body1 bold>{selectedDateTime.format("DD MMM YYYY")}</Body1>
          <NavBtn
            Icon={FiChevronRight}
            tertiary
            outline
            onClick={handleNextClick}
          />
        </DateSelection>
        <TimeSelection className="no-select">
          <TimeSlider
            onWheel={e => handleHourScroll(e.deltaY)}
            onTouchStart={handleTouchStart}
            onTouchMove={e => {
              const touch = e.touches[0];
              handleTouchMove(touch.clientY, handleHourScroll);
            }}
          >
            <SelectionSlot />
            <TimeList
              offset={hourTranslateY}
              showTransition={isManualScrollRef.current}
            >
              {hours.map(hour => {
                const isSelected = hour - 1 === selectedHourIndex;
                return (
                  <Slot
                    ref={setItemHeight}
                    key={hour}
                    onClick={() => handleHourSelection(hour)}
                  >
                    <DisplayTime bold selected={isSelected}>
                      {formatTime(hour)}
                    </DisplayTime>
                  </Slot>
                );
              })}
            </TimeList>
          </TimeSlider>
          <H3 bold>:</H3>
          <TimeSlider
            onWheel={e => handleMinuteScroll(e.deltaY)}
            onTouchStart={handleTouchStart}
            onTouchMove={e => {
              const touch = e.touches[0];
              handleTouchMove(touch.clientY, handleMinuteScroll);
            }}
          >
            <SelectionSlot />
            <TimeList
              offset={minuteTranslateY}
              showTransition={isManualScrollRef.current}
            >
              {minutes.map(minute => {
                const isSelected = minute / 5 === selectedMinuteIndex;
                return (
                  <Slot
                    key={minute}
                    selected={isSelected}
                    onClick={() => handleMinuteSelection(minute)}
                  >
                    <DisplayTime bold selected={isSelected}>
                      {formatTime(minute)}
                    </DisplayTime>
                  </Slot>
                );
              })}
            </TimeList>
          </TimeSlider>
          <FlexBox column rowGap="0.5rem">
            <Meridiem isSelected={isAm} onClick={handleAmClick}>
              <Body2 bold>AM</Body2>
            </Meridiem>
            <Meridiem isSelected={!isAm} onClick={handlePmClick}>
              <Body2 bold>PM</Body2>
            </Meridiem>
          </FlexBox>
        </TimeSelection>
      </Content>
      <Footer>
        <Body1 bold>{selectedDateTime.format("DD MMM YYYY, hh:mm A")}</Body1>
        <Button
          width="auto"
          disabled={isCtaDisabled}
          onClick={() => onTimeSelect(selectedDateTime.toISOString())}
        >
          DONE
        </Button>
      </Footer>
    </Modal>
  );
};

export default TimePicker;
